<template>
  <div class="tw-bg-[#FFF] tw-rounded tw-relative current_scan">
    <!--    header -->
    <div
      id="dash-latest-scan-title"
      class="tw-flex tw-px-[20px] tw-bg-[#FCFCFD] tw-justify-between tw-py-[25px] tw-text-[#211F58] tw-text-[18px] tw-font-semibold current_scan_title"
    >
      <span data-cy="dash-latest-scan-title">
        {{ getComponentUiText("app-dashboard-latest-scan-title-1") }}
        {{ `${currentScan?.name ? `- ${currentScan?.name || ""}` : ""}` }}
      </span>
    </div>

    <!--    body -->
    <div
      v-if="isLoading || !currentScan?.loading"
      class="custom-global-skeleton tw-w-full tw-h-[182px]"
    />
    <div
      v-else-if="
        (currentScan?.loading && currentScan?.runStatusId) || isShowDummyCard
      "
      class="tw-p-[20px]"
    >
      <div class="tw-gap-[20px] content_current_scan">
        <div class="tw-flex-1 tw-gap-[6px]">
          <div class="tw-flex tw-items-center tw-justify-between tw-pb-[6px]">
            <div
              class="tw-text-[13px] tw-font-medium tw-leading-[130%] tw-text-[#211F58]"
            >
              {{ getComponentUiText("app-manage-orgs-header-status-1") }}
            </div>
            <div
              :class="`tw-text-[11px] tw-font-[400] tw-rounded tw-text-[#878787] tw-leading-[130%] tw-py-[3px] tw-px-[5px] ${currentScan?.runStatusId}`"
            >
              {{ currentScan?.runStatus }}
            </div>
          </div>

          <div>
            <div class="tw-flex tw-items-center tw-justify-between tw-pb-[6px]">
              <span
                class="tw-text-[13px] tw-font-medium tw-leading-[130%] tw-text-[#211F58]"
                >{{
                  getComponentUiText(
                    "app-component-assessment-title-progress-1",
                  )
                }}
              </span>
              <span
                class="tw-text-[13px] tw-font-medium tw-text-[#878787] tw-leading-[130%]"
              >
                {{ getProgress(currentScan?.runStatusId) }}
              </span>
            </div>
          </div>
          <div>
            <div class="tw-flex tw-items-center tw-justify-between">
              <span
                class="tw-text-[13px] tw-font-medium tw-leading-[130%] tw-text-[#211F58]"
                >{{
                  getComponentUiText("app-scan-scanning-scan-elapsed-time-1")
                }}
              </span>
              <span
                class="tw-text-[13px] tw-font-[400] tw-text-[#878787] tw-leading-[130%]"
              >
                {{
                  getElapsedTime(currentScan?.startedAt, currentScan?.endedAt)
                }}
              </span>
            </div>
          </div>
        </div>
        <div class="tw-flex-1">
          <div class="tw-flex tw-items-center tw-justify-between tw-pb-[6px]">
            <div
              class="tw-text-[13px] tw-font-medium tw-leading-[130%] tw-text-[#211F58]"
            >
              {{ getComponentUiText("app-dashboard-current-scan-type-1") }}
            </div>
            <div
              class="tw-text-[11px] tw-font-medium tw-text-[#878787] tw-leading-[130%] type__scan"
            >
              {{ currentScan?.type }}
            </div>
          </div>

          <div>
            <div class="tw-flex tw-items-center tw-justify-between tw-pb-[6px]">
              <span
                class="tw-text-[13px] tw-font-medium tw-leading-[130%] tw-text-[#211F58]"
                >{{
                  getComponentUiText(
                    "app-component-assessments-checklists-assessment-details-card-label-1",
                  )
                }}
              </span>
              <span
                class="tw-text-[13px] tw-font-[400] tw-text-[#878787] tw-leading-[130%]"
              >
                {{ formatDate(currentScan?.startedAt) }}
              </span>
            </div>
          </div>
          <div>
            <div class="tw-flex tw-items-center tw-justify-between">
              <span
                class="tw-text-[13px] tw-font-medium tw-leading-[130%] tw-text-[#211F58]"
                >{{
                  getComponentUiText(
                    "app-dashboard-current-scan-time-remaining-1",
                  )
                }}
              </span>
              <span
                class="tw-text-[13px] tw-font-[400] tw-text-[#878787] tw-leading-[130%]"
              >
                {{ getComponentUiText("app-dashboard-current-scan-unknown-1") }}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div class="tw-pt-[20px]">
        <IssueNumberBlocks
          :display-border="false"
          :web-services-id="'1'"
          :domain-id="'1'"
          :severity="{
            critical: currentScan?.critical,
            high: currentScan?.high,
            medium: currentScan?.med,
            low: currentScan?.low,
          }"
        />
      </div>
    </div>
    <div v-else class="tw-h-[182px]" />
    <DummyCard
      v-if="isShowDummyCard && !isLoading"
      :title="getComponentUiText('app-dashboard-dummy-title-current-scan-1')"
      :title-button="
        getComponentUiText('app-dashboard-dummy-button-title-setup-scan-1')
      "
      :title-class="'tw-w-[276px]'"
      :custom-class="'tw-top-[67px]'"
      @button:click="() => emits('button:click')"
    />
  </div>
</template>

<script setup lang="ts">
import { getComponentUiText, getElapsedTime } from "@/utils/utilities";
import IssueNumberBlocks from "@/components/scanning/IssueNumberBlocks.vue";
import { computed, ref } from "vue";
import { convertUTCTimeToLocalTime } from "@/utils/dateTimeUtils";
import DummyCard from "./dummyCard.vue";
import dayjs from "dayjs";
const props = defineProps({
  currentScan: {
    type: Object,
    required: true,
  },
  isLoading: {
    type: Boolean,
    required: true,
  },
});

const emits = defineEmits(["button:click"]);

const isShowDummyCard = computed(() => {
  return props.currentScan?.loading && !props.currentScan?.runStatusId;
});

const listStatusId = ref<any>({
  ok: "100%",
  start: "0%",
  inprog: "50%",
  time: "100%",
  canc: "100%",
  fail: "100%",
  cap: "100%",
});
const getProgress = (statusId) => {
  return listStatusId.value[statusId];
};

const formatDate = (isoDateString) => {
  if (isoDateString) {
    return convertUTCTimeToLocalTime(
      isoDateString.toString(),
      undefined,
      "HH:mm",
    );
  }
};
</script>

<style lang="scss" scoped>
.inProgress,
.inprog,
.start {
  background: linear-gradient(
      0deg,
      rgba(72, 160, 255, 0.15) 0%,
      rgba(72, 160, 255, 0.15) 100%
    ),
    #fff;
  color: #387dff;
}

.defunct {
  background: #fff;
  border: 1px solid var(--ui-orange-soft, #fee5e2);
  color: #f7553c;
}

.complete,
.ok {
  background: #fff;
  border: 1px solid var(--ui-green-soft, #d9ede2);
  color: #01893d;
}

.audited {
  background: #fff;
  border: 1px solid #eee4f9;
  color: #904bd6;
}

.onHold {
  background: #fff;
  border: 1px solid #fff0da;
  color: #fe9a05;
}

.notStarted,
.err,
.time,
.canc,
.fail,
.cap {
  background: #fff;
  border: 1px solid #fee5e2;
  color: #f7553c;
}
.current_scan_title {
  border-bottom: 1px solid var(--UI-rule, #eff0f1);
  line-height: 1rem;
}
.current_scan {
  border-radius: 4px;
  border: 1px solid var(--UI-rule, #eff0f1);
  background: var(--UI-white, #fff);
  box-shadow: 0px 0px 4px 0px rgba(0, 0, 0, 0.05);
}
.type__scan {
  display: flex;
  padding: 3px 5px;
  justify-content: center;
  align-items: center;

  border-radius: 4px;
  border: 1px solid var(--UI-rule, #eff0f1);
  background: var(--UI-white, #fff);
}
.content_current_scan {
  display: flex;
}
@media only screen and (max-width: 500px) {
  .content_current_scan {
    flex-direction: column;
  }
}
</style>
